import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PharmacyLayout } from './PharmacyLayout';
import {
  usePaginatedPharmacyPurchaseOrders,
  useCancelPharmacyPurchaseOrder,
  useBulkDeletePharmacyPurchaseOrders,
} from '../../services/httpServices/queries';
import type { PharmacyPurchaseOrderStatus } from '../../services/httpServices/pharmacyPurchasingService';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { useDebounce } from '../../hooks/useDebounce';
import { Plus, Search, Loader2, AlertCircle, Eye, Truck, XCircle, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const LIMIT = 20;

const statusStyles: Record<PharmacyPurchaseOrderStatus, string> = {
  DRAFT: 'bg-background text-text-muted border border-border',
  SENT: 'bg-primary/10 text-primary',
  PARTIALLY_RECEIVED: 'bg-warning/10 text-warning',
  RECEIVED: 'bg-success/10 text-success',
  CANCELLED: 'bg-danger/10 text-danger',
};

const statusLabels: Record<PharmacyPurchaseOrderStatus, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  PARTIALLY_RECEIVED: 'Partially Received',
  RECEIVED: 'Received',
  CANCELLED: 'Cancelled',
};

const filters: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SENT', label: 'Sent' },
  { value: 'PARTIALLY_RECEIVED', label: 'Partial' },
  { value: 'RECEIVED', label: 'Received' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export function PharmacyPurchases() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = usePaginatedPharmacyPurchaseOrders(page, LIMIT, {
    status: status || undefined,
    search: debouncedSearch.trim() || undefined,
  });
  const orders = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;

  const cancelMutation = useCancelPharmacyPurchaseOrder();
  const bulkDeleteMutation = useBulkDeletePharmacyPurchaseOrders();

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [status, debouncedSearch]);

  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(orders.map((o) => o.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [orders]);

  const allSelected = orders.length > 0 && orders.every((o) => selectedIds.has(o.id));
  const someSelected = orders.some((o) => selectedIds.has(o.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = orders.length > 0 && orders.every((o) => prev.has(o.id));
      return everySelected ? new Set() : new Set(orders.map((o) => o.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      const res = await bulkDeleteMutation.mutateAsync(ids);
      const deleted = res.data?.deleted?.length ?? 0;
      const blocked = res.data?.blocked?.length ?? 0;
      if (blocked > 0) {
        toast.warning(
          `Moved ${deleted} order(s) to trash. ${blocked} skipped — only draft or cancelled orders without receipts can be removed.`,
        );
      } else {
        toast.success(`Moved ${deleted} order(s) to trash`);
      }
      setSelectedIds(new Set());
    } catch {
      toast.error('Failed to move some purchase orders to trash');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  return (
    <PharmacyLayout
      title="Purchase Orders"
      description="Order medicines from suppliers and receive them into stock"
      action={
        <>
          <button
            onClick={() => navigate('/pharmacy/purchasing-trash')}
            className="px-3 py-2 bg-surface border border-border text-text-muted rounded-lg hover:text-text hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
          >
            <Trash2 size={16} />
            Trash
          </button>
          <button
            onClick={() => navigate('/pharmacy/purchases/new')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
          >
            <Plus size={16} />
            New Purchase Order
          </button>
        </>
      }
    >
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {filters.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatus(f.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                status === f.value
                  ? 'bg-primary text-white'
                  : 'text-text-muted hover:text-text hover:bg-background'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder="Search by PO number or supplier..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-9 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-danger"
              title="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Move to trash
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr>
                <th className="px-4 py-3 w-10 text-left">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={orders.length === 0}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                    title="Select all"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">PO Number</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Supplier</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Order Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-danger">
                    <AlertCircle size={18} className="inline mr-2" /> Failed to load purchase orders
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-text-muted">
                    {debouncedSearch ? 'No purchase orders match your search.' : 'No purchase orders found.'}
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(order.id)}
                        onChange={() => toggleOne(order.id)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 font-mono font-medium text-text">{order.poNumber}</td>
                    <td className="px-4 py-3 text-text">{order.supplier?.name || '—'}</td>
                    <td className="px-4 py-3 text-text-muted">
                      {order.orderDate ? new Date(order.orderDate).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-text">
                      {Number(order.totalAmount).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusStyles[order.status]}`}>
                        {statusLabels[order.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/pharmacy/purchases/${order.id}`)}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-text-muted hover:text-primary hover:bg-primary/10 transition-colors flex items-center gap-1"
                        >
                          <Eye size={14} /> View
                        </button>
                        {order.status !== 'RECEIVED' && order.status !== 'CANCELLED' && (
                          <>
                            <button
                              onClick={() => navigate(`/pharmacy/purchases/${order.id}/receive`)}
                              className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-success/10 text-success hover:bg-success/20 transition-colors flex items-center gap-1"
                            >
                              <Truck size={14} /> Receive
                            </button>
                            <button
                              onClick={() => cancelMutation.mutate(order.id)}
                              disabled={cancelMutation.isPending}
                              className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-danger hover:bg-danger/10 transition-colors disabled:opacity-50 flex items-center gap-1"
                            >
                              <XCircle size={14} /> Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={meta?.page || page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={LIMIT}
          onPageChange={setPage}
          itemName="purchase orders"
        />
      </div>

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Move ${selectedIds.size} purchase order${selectedIds.size === 1 ? '' : 's'} to trash?`}
        description="Only draft or cancelled orders without goods receipts can be removed; others are skipped."
        confirmLabel={`Move ${selectedIds.size} to trash`}
        loading={bulkDeleteMutation.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </PharmacyLayout>
  );
}
